import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TourResult, TourRoute } from "@/types/tour";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TourTimeline } from "@/components/TourTimeline";
import { TourMap } from "@/components/TourMap";
import { ShareRoute } from "@/components/ShareRoute";

interface TourResultsProps {
  results: TourResult;
}

export function TourResults({ results }: TourResultsProps) {
  const { t } = useTranslation();
  const [activeRoute, setActiveRoute] = useState(results.routes[0]?.id || "");

  const currentRoute = results.routes.find((r) => r.id === activeRoute) || results.routes[0];

  return (
    <div className="space-y-6">
      {results.startTimeAdjustmentReason && (
        <div className="rounded-lg border border-primary/40 bg-primary/10 p-3 sm:p-4 text-xs sm:text-sm text-primary">
          ⏰ {results.startTimeAdjustmentReason}
        </div>
      )}
      <Tabs value={activeRoute} onValueChange={setActiveRoute}>
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${results.routes.length}, 1fr)` }}>
          {results.routes.map((route) => (
            <TabsTrigger key={route.id} value={route.id} className="text-xs sm:text-sm">
              <span className="sm:hidden text-base">{route.icon}</span>
              <span className="hidden sm:inline">{route.icon} {route.name}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {results.routes.map((route) => (
          <TabsContent key={route.id} value={route.id} className="space-y-6">
            {/* Route summary */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="rounded-lg border border-border bg-card p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold text-primary">{route.schedule.length}</p>
                <p className="text-xs text-muted-foreground">{t("results.bars")}</p>
              </div>
              <div className="rounded-lg border border-border bg-card p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold text-primary">{route.totalWalkingMinutes} min</p>
                <p className="text-xs text-muted-foreground">{t("results.walking")}</p>
              </div>
              <div className="rounded-lg border border-border bg-card p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold text-primary">⭐ {route.averageRating.toFixed(1)}</p>
                <p className="text-xs text-muted-foreground">{t("results.rating")}</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">{route.description}</p>

            {/* Map */}
            <TourMap route={route} center={results.center} />

            {/* Timeline */}
            <TourTimeline schedule={route.schedule} />

            {/* Share */}
            <ShareRoute route={route} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
